// Toolkit "Best Practices" content — static-first (EventPay-authored).
//
// Shape mirrors the future backend `MarketingResource` model so this can be
// lifted into an API without a redesign: { audience, type, title, summary,
// body, tags, order }. Body is markdown-lite: rendered as paragraphs +
// bullet lists ("- " prefix) by the Toolkit page — no MDX dependency needed
// for launch.
//
// Merge placeholders allowed in body: {event_name}, {token_name}.

export type ResourceAudience = 'attendees' | 'vendors' | 'team'

export interface ToolkitResource {
  audience: ResourceAudience
  /** 'tip' renders inline; 'guide' opens the detail view. */
  type: 'tip' | 'guide'
  title: string
  summary: string
  body: string
  order: number
}

export const TOOLKIT_RESOURCES: ToolkitResource[] = [
  // ── For Attendees ────────────────────────────────────────────────────────
  {
    audience: 'attendees',
    type: 'guide',
    title: 'Set up before you arrive',
    summary: 'Get guests app-ready before the gates so they skip the top-up lines.',
    order: 1,
    body: `The single biggest line-killer is attendees arriving with a loaded wallet. Push this message in the week before {event_name}.

What to tell attendees:
- Download the app and sign in before event day — takes two minutes.
- Add {token_name} in advance so you can head straight to the food.
- Your balance is tied to your phone number — no physical tickets to lose.

Where to say it:
- Include your event link (from Share & QR above) in every email and social post.
- Pin a "How to pay" post the day before doors.
- Ask ticketing partners to include the link in confirmation emails.`,
  },
  {
    audience: 'attendees',
    type: 'guide',
    title: 'How to pay at the event',
    summary: 'A simple explainer for first-timers at vendor booths.',
    order: 2,
    body: `Most first-time guests have never used event tokens. One clear explainer at entry removes 90% of the confusion.

The 3 steps guests need to know:
- Web & App based, it's simple to signup.
- Show the QR at any vendor booth; the vendor scans and you're done.
- Top up anytime from the wallet screen — no need to visit a booth.

Placement tips:
- Print the QR poster (Share & QR above) at every entrance.
- Put table tents at bars and high-traffic vendors.
- Brief your gate staff on the 3 steps — they're your front line.`,
  },
  {
    audience: 'attendees',
    type: 'guide',
    title: 'Refunds & leftover tokens',
    summary: 'Set expectations early so support lines stay short at close.',
    order: 3,
    body: `End-of-night refund questions spike when policy isn't visible. Decide and publish your policy before doors.

Decide up front:
- Are leftover {token_name} refundable, and until when?
- Where do guests go with a problem — a booth, or in-app support?

Communicate it:
- Add one line to your event page and entry signage ("Unused tokens are refundable until …").
- Brief vendors: they should point guests to the info point, not debate policy at the vendor booth.
- Your team can issue refunds from the dashboard (Transactions → refund).`,
  },

  // ── For Vendors ─────────────────────────────────────────────────────────
  {
    audience: 'vendors',
    type: 'guide',
    title: 'Get set up before doors',
    summary: 'The pre-event checklist that prevents day-of fires.',
    order: 1,
    body: `A vendor who tests their setup the day before is a vendor you never hear from during service.

Checklist to send every vendor:
- Confirm you can sign in to the vendor app — reset access now, not when the event starts.
- Check your menu: items, prices, and availability may be editable from your vendor account.
- Familiarize yourself with the vendor app, it's simple to use.

Organizer side:
- Use the bulk import (Vendors page) to load menus early.
- Resend activation emails for any admin who hasn't signed in.`,
  },
  {
    audience: 'vendors',
    type: 'guide',
    title: 'Taking orders smoothly',
    summary: 'Rush-hour patterns that keep queues moving.',
    order: 2,
    body: `Vendors process orders fastest when scanning is second nature.

Coach vendors to:
- Keep the POS app open during service — every tap saved matters in a rush.
- Mark items unavailable the moment they sell out, so guests stop ordering them.
- Watch the orders queue if order-ahead is on — accept promptly so guests aren't waiting on a silent order.

Common hiccups:
- A QR that won't scan is usually screen brightness — ask the guest to turn it up.
- If a guest's balance is short, they can top up in the app on the spot — no need to leave the line.`,
  },
  {
    audience: 'vendors',
    type: 'guide',
    title: 'End-of-day payout',
    summary: 'Where vendors see their numbers, so they don\'t call you.',
    order: 3,
    body: `Every vendor question at close is really one question: "how much did we sell?"

Point vendors to:
- Their vendor dashboard — live sales, orders, and item breakdowns all day.
- The revenue summary at close — same numbers you see on your side.

Organizer side:
- Agree the settlement schedule with vendors before the event.
- Use the Financials page for the event-wide reconciliation.
- Export per-vendor reports from Reporting when vendors want statements.`,
  },

  // ── For Your Team ───────────────────────────────────────────────────────
  {
    audience: 'team',
    type: 'guide',
    title: 'Day-of runbook',
    summary: 'Roles, escalation, and the support flow for guest issues.',
    order: 1,
    body: `The teams that have calm events decided *who handles what* before doors.

Assign owners for:
- Gate/entry — helping guests install and top up (your busiest hour is the first one).
- A visible info point — where vendors and guests are sent for anything payment-related.
- One dashboard owner — watching live stats and triaging.

Escalation path:
- Front-line staff → info point → dashboard owner (who has the admin permissions).
- Use Support chat in this dashboard to look up any attendee, order, or transaction fast — and to action refunds with a confirm step.`,
  },
  {
    audience: 'team',
    type: 'guide',
    title: 'Reading your dashboard',
    summary: 'The live numbers that matter and what to do when they move.',
    order: 2,
    body: `During the event, three numbers tell you almost everything:

- Tokens issued vs spent — a wide gap late in the day means slow vendor lines or guests who don't know where to spend. Nudge with signage or announcements.
- Orders per hour (Live Stats) — a sudden drop at one vendor usually means a device or staffing issue at that booth.
- Top items — sell-outs show up here first; get vendors marking items unavailable.

Habit to build: check Live Stats at the top of each hour, not just when something feels wrong.`,
  },
  {
    audience: 'team',
    type: 'guide',
    title: 'Handling refunds & lost phones',
    summary: 'Safe, correct front-line handling for the two most common requests.',
    order: 3,
    body: `Refunds:
- Refunds are issued from the dashboard (Transactions or an order's detail) by staff with the refund permission.
- The dashboard asks for your password if you haven't signed in recently — that's expected, it protects against a stolen session.
- Never promise cash refunds at a booth; route to the info point.

Lost/dead phone:
- The guest's balance is on their account, not the device — sign in on any phone recovers it.
- Staff can look up the account by phone number or email from Users (or Support chat) to confirm the balance while the guest recovers access.`,
  },
]
